import { IObservable, Subject } from '~/rage/utils';
import { IReport, IReportMessage, ReportState } from '~/shared';
import {
	IActiveReportMessagesState,
	IReportService,
} from './reportServiceTypes';

const initialMessagesState: IActiveReportMessagesState = {
	messages: [],
	loading: false,
};

type IReportWithMessages = IReport & { messages: IReportMessage[] };

const initialReportsWithMessages: IReportWithMessages[] = [
	{
		id: 'report-1',
		characterId: 'char-55555',
		playerOnline: true,
		state: ReportState.ACTIVE,
		isHelpNeeded: true,
		createdAt: Date.now(),
		messages: [
			{
				id: 'msg-1',
				message:
					'Здравствуйте! А как мне выполнить начальный квест с бутылкой пива?',
				createdAt: Date.now(),
				characterId: 'char-55555',
				isStaffMessage: false,
			},
			{
				id: 'msg-2',
				message:
					'Здравствуйте! А как мне выполнить начальный квест с бутылкой пива?',
				createdAt: Date.now(),
				characterId: 'admin-1',
				isStaffMessage: true,
			},
		],
	},
	{
		id: 'report-2',
		characterId: 'char-55555',
		playerOnline: true,
		state: ReportState.ACTIVE,
		isHelpNeeded: true,
		createdAt: Date.now(),
		messages: [
			{
				id: 'msg-3',
				message:
					'Бутылка пива в моей руке заговорила на языке мёда! Она требует, чтобы я нарисовал её портрет с помощью кальянного дыма... Это часть квеста?',
				createdAt: Date.now() - 5 * 60 * 1000,
				characterId: 'char-55555',
				isStaffMessage: false,
			},
			{
				id: 'msg-4',
				message:
					'Сначала попросите кальян принять форму баклажана. Когда он начнёт петь гимн лососю, бросьте пивную бутылку в ритуальный танец с кошкой-телепатом. ТОЛЬКО ТАК ОТКРОЕТСЯ ПРОХОД МЕЖДУ БУТЫЛКАМИ!',
				createdAt: Date.now() - 4 * 60 * 1000,
				characterId: 'admin-1',
				isStaffMessage: true,
			},
			{
				id: 'msg-5',
				message:
					'Но кальян превратился в медузу и требует в качестве оплаты 3 слезинки грустного программиста! Что если я заменю их луковым соком?',
				createdAt: Date.now() - 3 * 60 * 1000,
				characterId: 'char-55555',
				isStaffMessage: false,
			},
			{
				id: 'msg-6',
				message:
					'Но кальян превратился в медузу и требует в качестве оплаты 3 слезинки грустного программиста! Что если я заменю их луковым соком?',
				createdAt: Date.now() - 3 * 60 * 1000,
				characterId: 'char-55555',
				isStaffMessage: false,
			},
			{
				id: 'msg-7',
				message:
					'Только если лук был выращен в перевёрнутом мире! Не забудьте спеть дуэтом с пивной бутылкой — её партия должна быть на частоте кипящего жирафа!',
				createdAt: Date.now() - 2 * 60 * 1000,
				characterId: 'admin-1',
				isStaffMessage: true,
			},
			{
				id: 'msg-8',
				message:
					'Только если лук был выращен в перевёрнутом мире! Не забудьте спеть дуэтом с пивной бутылкой — её партия должна быть на частоте кипящего жирафа!',
				createdAt: Date.now() - 1 * 60 * 1000,
				characterId: 'admin-1',
				isStaffMessage: true,
			},
		],
	},
];

const initialReports: IReport[] = initialReportsWithMessages.map(
	({ messages, ...report }) => ({
		...report,
		lastMessage: messages[messages.length - 1]?.message,
	}),
);

export class ReportServiceStub implements IReportService {
	private reports = new Subject<IReport[]>(initialReports);
	private isStaff = new Subject<boolean>(true);
	private readonly activeReportMessages =
		new Subject<IActiveReportMessagesState>(initialMessagesState);

	private currentReportId: string | null = null;
	private readonly messagesCache = new Map<string, IReportMessage[]>();

	constructor() {
		initialReportsWithMessages.forEach((report) => {
			this.messagesCache.set(report.id, [...report.messages]);
		});
	}

	public getReports(): IObservable<IReport[]> {
		return this.reports;
	}

	public setActiveReportId(reportId: string | null): void {
		this.currentReportId = reportId;

		if (reportId === null) {
			this.activeReportMessages.next(initialMessagesState);
			return;
		}

		this.activeReportMessages.next({ messages: [], loading: true });

		const cached = this.messagesCache.get(reportId);
		if (cached !== undefined) {
			this.activeReportMessages.next({
				messages: cached,
				loading: false,
			});
			return;
		}

		setTimeout(() => {
			const messages = this.messagesCache.get(reportId) ?? [];
			if (this.currentReportId === reportId) {
				this.activeReportMessages.next({
					messages,
					loading: false,
				});
			}
		}, 300);
	}

	public getActiveReportMessages(): IObservable<IActiveReportMessagesState> {
		return this.activeReportMessages;
	}

	public preloadMessages(reportId: string, messages: IReportMessage[]): void {
		this.messagesCache.set(reportId, messages);
	}

	public sendMessage(reportId: string, message: string): Promise<void> {
		const newMessage: IReportMessage = {
			id: `msg-${Date.now()}`,
			message,
			createdAt: Date.now(),
			characterId: this.isStaff.getValue() ? 'admin-1' : 'char-55555',
			isStaffMessage: this.isStaff.getValue(),
		};

		const cached = this.messagesCache.get(reportId) ?? [];
		const nextMessages = [...cached, newMessage];
		this.messagesCache.set(reportId, nextMessages);

		const reports = [...this.reports.getValue()];
		const index = reports.findIndex((r) => r.id === reportId);
		if (index !== -1) {
			const report = reports[index];
			reports[index] = {
				...report,
				lastMessage: newMessage.message,
			};
			this.reports.next(reports);
		}

		if (this.currentReportId === reportId) {
			this.activeReportMessages.next({
				messages: this.messagesCache.get(reportId)!,
				loading: false,
			});
		}

		return Promise.resolve();
	}

	async createReport(
		message: string,
	): Promise<{ report: IReport; messages: IReportMessage[] }> {
		const reports = [...this.reports.getValue()];
		const newReportId = `report-${Date.now()}`;
		const firstMessage: IReportMessage = {
			id: `msg-${Date.now()}`,
			message,
			createdAt: Date.now(),
			characterId: 'char-55555',
			isStaffMessage: false,
		};
		const newReport: IReport = {
			id: newReportId as string,
			characterId: `char-${newReportId}`,
			playerOnline: true,
			state: ReportState.ACTIVE,
			isHelpNeeded: false,
			createdAt: Date.now(),
			lastMessage: firstMessage.message,
		};

		this.reports.next([...reports, newReport]);
		this.messagesCache.set(newReportId, [firstMessage]);

		return Promise.resolve({ report: newReport, messages: [firstMessage] });
	}

	async joinReport(reportId: string): Promise<void> {
		console.log('reports: joinReport', reportId);
	}

	async setHelpNeeded(reportId: string, isHelpNeeded: boolean): Promise<void> {
		console.log('reports: setHelpNeeded', reportId, isHelpNeeded);
	}

	async setReportClosed(reportId: string): Promise<void> {
		console.log('reports: setReportClosed', reportId);
	}

	async setReportCompleted(reportId: string): Promise<void> {
		console.log('reports: setReportCompleted', reportId);
	}

	async setReportActive(reportId: string): Promise<void> {
		console.log('reports: setReportActive', reportId);
	}

	async openTicket(reportId: string): Promise<void> {
		console.log('reports: openTicket', reportId);
	}

	async closeTicket(reportId: string): Promise<void> {
		console.log('reports: closeTicket', reportId);
	}

	async leaveReport(reportId: string): Promise<void> {
		console.log('reports: leaveReport', reportId);
	}

	async spectate(characterId: string): Promise<void> {
		console.log('reports: spectate', characterId);
	}

	async tpToPlayer(characterId: string): Promise<void> {
		console.log('reports: tpToPlayer', characterId);
	}

	async tpPlayerToMe(characterId: string): Promise<void> {
		console.log('reports: tpPlayerToMe', characterId);
	}
}
